var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useContext, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import NextLink from 'next/link';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import useContentfulFeature from 'shop/lib/util/client.util/featureFlag';
import { isPreview } from '@bombas/react/util';
import { ApplicationContext } from 'shop/context/ApplicationContext';
import * as Shared from '../../ContentSystem.shared.styles';
import ProductCard from '../../../ProductCard/ProductCard';
import ProductCardWithQuickAdd from '../../../ProductCard/ProductCardWithQuickAdd';
import * as Styled from './CardSection.styled';
import { convertLayoutOptionToNumber, getCardTextSizeFromCardWidth, getDefaultCardLayout, getVisibleSlidesCount, numSlidesCalc, contentCardimageResolutionsByLayout, contentCardimageResolutionsByGalleryLayout, needsSelectCarousel, isSmallContainer } from './CardSection.utils';
import { useBreakpoint } from '../../../../util/Breakpoint.context';
import { vercel_base_url } from '../../../../util/config';
import { CardSectionFragment } from './CardSection.gql';
import { DEFAULT_HEADER_MAX_LINE_LENGTH } from '../../ContentSystem.shared.styles';
import VideoPlayer from '../../../VideoPlayer/VideoPlayer';
import useAspectRatio from '../../../VideoPlayer/VideoPlayer.hooks';
import ThemeProvider from '../../context/ThemeContext';
import { CARD_SECTION_IMAGE_RESOLUTIONS } from './CardSectionImageResolutions';
import QuickAddModal from '../../../QuickAddModal/QuickAddModal';
import QuickAddModalProvider from '../../../QuickAddModal/QuickAddModal.context';
import { SITE_CONFIG } from '../../../App.gql';
import TextToJSX from '../TextToJSX/TextToJSX';
import SecondaryHeader from '../SecondaryHeader/SecondaryHeader';
import { ContainerQueryContext } from '../../../../util/ContainerQuery.context';
import { cleanProductTitle, enhanceAnyProductWithCallouts } from '../../../Product/Product.utils';
function isProduct(value) {
    return value.__typename === 'Product';
}
function isVideoPlayer(value) {
    return value.__typename === 'Video';
}
var CardSection = function (_a) {
    var _b;
    var _c = _a.backgroundColor, backgroundColor = _c === void 0 ? 'white' : _c, _d = _a.className, className = _d === void 0 ? '' : _d, _e = _a.cards, cards = _e === void 0 ? [] : _e, _f = _a.content, content = _f === void 0 ? undefined : _f, _g = _a.ctas, ctas = _g === void 0 ? [] : _g, customBackgroundColor = _a.customBackgroundColor, indentGrid = _a.indentGrid, _h = _a.horizontalSpace, horizontalSpace = _h === void 0 ? 'COMFORTABLE' : _h, _j = _a.layoutDesktop, layoutDesktop = _j === void 0 ? 'ACROSS_2' : _j, layoutMobile = _a.layoutMobile, layoutTablet = _a.layoutTablet, _k = _a.overflowBehavior, overflowBehavior = _k === void 0 ? 'WRAP' : _k, _l = _a.removeSpace, removeSpace = _l === void 0 ? 'NONE' : _l, cardheaderAlignment = _a.cardheaderAlignment, _m = _a.textColor, textColor = _m === void 0 ? 'AUTO' : _m, _o = _a.verticalSpace, verticalSpace = _o === void 0 ? 'COMFORTABLE' : _o, _p = _a.headerMaxLineLength, headerMaxLineLength = _p === void 0 ? DEFAULT_HEADER_MAX_LINE_LENGTH : _p, _q = _a.withSidePadding, withSidePadding = _q === void 0 ? true : _q, _r = _a.layout, layout = _r === void 0 ? 'GRID' : _r, horizontalAlignment = _a.horizontalAlignment, _s = _a.verticalAlignment, verticalAlignment = _s === void 0 ? 'MIDDLE' : _s, _t = _a.indentGallery, indentGallery = _t === void 0 ? false : _t, _u = _a.eyebrow, eyebrow = _u === void 0 ? '' : _u, gutter = _a.gutter, settings = _a.settings, dataLabel = _a.dataLabel, _v = _a.mobileImage, mobileImage = _v === void 0 ? undefined : _v, _w = _a.tabletImage, tabletImage = _w === void 0 ? undefined : _w, _x = _a.desktopImage, desktopImage = _x === void 0 ? undefined : _x, _y = _a.backgroundImageCrop, backgroundImageCrop = _y === void 0 ? 'CROP_TO_CONTENT' : _y, breadcrumb = _a.breadcrumb, _z = _a.arrowsPosition, arrowsPosition = _z === void 0 ? 'INLINE' : _z, breadcrumbDataLabel = _a.breadcrumbDataLabel;
    /**
     * Image fallbacks according to the following rules:
     */
    var largeScreenImage = desktopImage || tabletImage || mobileImage;
    var mediumScreenImage = tabletImage || mobileImage || desktopImage;
    var smallScreenImage = mobileImage || tabletImage || desktopImage;
    var isBackgroundImage = Boolean(largeScreenImage);
    var _0 = useState(1), currentSlide = _0[0], setCurrentSlide = _0[1];
    /**
     * If there is no custom data label, use the default label
     */
    var sectionDataLabel = dataLabel || 'cardSection_noLabel';
    /**
     * By default, shouldNotCropContent is false
     * If there is an image and backgroundImageCrop is no_crop, then set shouldNotCropContent to true
     */
    var shouldNotCropContent = isBackgroundImage && backgroundImageCrop === 'NO_CROP';
    // Check if PCCS feature flag is enabled
    var enableProductsInGrid = useContentfulFeature('pccs');
    var productCardsConfig = useContext(ApplicationContext).productCardsConfig;
    // Get large screen layout option (which is requried) and derive its defaults
    var _1 = useBreakpoint(), medium = _1.medium, large = _1.large, largest = _1.largest, xlarge = _1.xlarge;
    // Get medium and small screen options or their fallbacks
    var isHorizontalComfortableSize = horizontalSpace === 'COMFORTABLE';
    var isHorizontalSpaceNone = horizontalSpace === 'NONE';
    var isGalleryLayout = layout === 'GALLERY';
    // Get site settings for discount copy
    var siteData = useQuery(SITE_CONFIG, {
        variables: { key: 'site', preview: isPreview('settings') }
    }).data;
    var siteConfig = (_b = siteData === null || siteData === void 0 ? void 0 : siteData.settings[0]) === null || _b === void 0 ? void 0 : _b.values;
    // Filter and enhance cards with product callouts in a single optimized operation
    // This consolidates filtering, limiting, and enhancement to minimize re-renders
    var enhancedCardsToDisplay = useMemo(function () {
        // Filter cards based on PCCS feature flag
        var filteredCards = cards.filter(function (card) {
            if (isProduct(card)) {
                return enableProductsInGrid;
            }
            return true;
        });
        // Limit cards for gallery layout
        var cardsToDisplay = isGalleryLayout
            ? filteredCards.slice(0, 2)
            : filteredCards;
        // Early return if no product cards config
        if (!productCardsConfig) {
            return cardsToDisplay;
        }
        // Enhance products with callouts in a single pass
        return cardsToDisplay.map(function (card) {
            if (isProduct(card) && (productCardsConfig === null || productCardsConfig === void 0 ? void 0 : productCardsConfig.length) > 0) {
                var enhancedProduct = enhanceAnyProductWithCallouts(card, productCardsConfig);
                // If the product has callouts, clean the title
                if (Array.isArray(enhancedProduct.callouts) &&
                    enhancedProduct.callouts.length > 0) {
                    enhancedProduct.title = cleanProductTitle(enhancedProduct.title);
                }
                return enhancedProduct;
            }
            return card;
        });
    }, [cards, enableProductsInGrid, isGalleryLayout, productCardsConfig]);
    var _2 = getDefaultCardLayout(layoutDesktop), defaultMediumScreenAcross = _2[0], defaultSmallScreenAcross = _2[1];
    var largeScreenAcross = convertLayoutOptionToNumber(layoutDesktop);
    var mediumOptions = layoutTablet || defaultMediumScreenAcross;
    var mediumScreenAcross = convertLayoutOptionToNumber(mediumOptions);
    var smallOptions = layoutMobile || defaultSmallScreenAcross;
    var smallScreenAcross = convertLayoutOptionToNumber(smallOptions);
    var cardsAcrossByScreenSize = [
        { screenSize: 'LARGE', across: layoutDesktop },
        { screenSize: 'MEDIUM', across: mediumOptions },
        { screenSize: 'SMALL', across: smallOptions }
    ];
    var responsiveTextSizes = cardsAcrossByScreenSize.map(function (_a) {
        var screenSize = _a.screenSize, across = _a.across;
        return ({
            screenSize: screenSize,
            textSize: getCardTextSizeFromCardWidth({
                screenSize: screenSize,
                width: across
            }) || 'REGULAR'
        });
    });
    /**
     * This is a special case
     * When 5 across is chosen, a 10 column grid is rendered on large screens only instead of a 12 column one to fit 5 cards in a row
     */
    var isTenColumnGrid = largeScreenAcross === 5;
    var isCarousel = layout === 'GRID' && overflowBehavior === 'CAROUSEL';
    // When true, the header and cards are centered
    // A flex grid is created to support centering cards
    var isCentered = cardheaderAlignment === 'CENTER';
    var getUseAspectRatio = function (videoId) {
        var aspectRatio = useAspectRatio(videoId, 'vimeo');
        return aspectRatio;
    };
    var _3 = useContext(ContainerQueryContext) || {}, _4 = _3.containerQueryClassName, containerQueryClassName = _4 === void 0 ? '' : _4, styleQueryMode = _3.styleQueryMode;
    var isContainerMode = styleQueryMode === 'container';
    /**
     * Determines the current layout based on the screen size or container size.
     */
    var getCurrentLayout = function () {
        if (isContainerMode) {
            if (isSmallContainer(containerQueryClassName)) {
                return smallOptions || 'ACROSS_4';
            }
            if (containerQueryClassName.includes('medium') &&
                containerQueryClassName.includes('less-than-large')) {
                return mediumOptions;
            }
            return layoutDesktop;
        }
        if (largest || xlarge || large)
            return layoutDesktop;
        if (medium)
            return mediumOptions;
        return smallOptions || 'ACROSS_4';
    };
    var currentLayout = getCurrentLayout();
    var visibleSlidesCount = parseInt(currentLayout.split('_')[1], 10);
    var isLarge = isContainerMode
        ? !containerQueryClassName.includes('less-than-large')
        : !!large;
    var calculatedVisibleSlidesCount = getVisibleSlidesCount(visibleSlidesCount, isLarge);
    var hasOverflow = enhancedCardsToDisplay.length > calculatedVisibleSlidesCount;
    var isLargeScreenWithSmallContainerQuery = needsSelectCarousel({
        styleQueryMode: styleQueryMode,
        containerQueryClassName: containerQueryClassName,
        hasOverflow: hasOverflow
    });
    var isManualArrowAbove = arrowsPosition === 'ABOVE' && isLarge;
    var showExternalControls = isLargeScreenWithSmallContainerQuery || isManualArrowAbove;
    var carouselArrowMode = showExternalControls ? 'external' : 'native';
    var BASE_SLIDE_POSITIONS = 1;
    // Extra peek step in small container for external carousel so the last cards fully show
    var EXTRA_PEEK_POSITION = 2;
    var externalCarouselOverflowCount = enhancedCardsToDisplay.length - calculatedVisibleSlidesCount;
    var externalCarouselOffset = isLargeScreenWithSmallContainerQuery
        ? EXTRA_PEEK_POSITION
        : BASE_SLIDE_POSITIONS;
    var effectiveTotalSlides = Math.max(1, externalCarouselOverflowCount + externalCarouselOffset);
    var carouselProps = {
        visibleSlidesCount: calculatedVisibleSlidesCount,
        numSlides: {
            small: Number(numSlidesCalc(smallScreenAcross, defaultSmallScreenAcross)),
            medium: Number(numSlidesCalc(mediumScreenAcross, defaultMediumScreenAcross)),
            large: Number(largeScreenAcross)
        },
        withPeeking: false,
        isInSmallContainer: isLargeScreenWithSmallContainerQuery
    };
    var getGalleryImageResolutions = function () {
        if (isContainerMode) {
            if (isSmallContainer(containerQueryClassName)) {
                return contentCardimageResolutionsByGalleryLayout.get('ACROSS_1');
            }
            return contentCardimageResolutionsByGalleryLayout.get('ACROSS_2');
        }
        if (medium || large || largest || xlarge) {
            return contentCardimageResolutionsByGalleryLayout.get('ACROSS_2');
        }
        return contentCardimageResolutionsByGalleryLayout.get('ACROSS_1');
    };
    var cardIndex = 0;
    var productCardIndex = 0;
    var RENDERED_CARDS = enhancedCardsToDisplay.map(function (card) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        // Increment the card index for data labels
        if (isProduct(card)) {
            productCardIndex += 1;
        }
        else {
            cardIndex += 1;
        }
        if (isProduct(card)) {
            var productCardDataLabel = "".concat(sectionDataLabel, "_productCard").concat(productCardIndex);
            return isCarousel ? (React.createElement(Styled.Card, { as: ProductCard, dataLabel: productCardDataLabel, url: "".concat(vercel_base_url).concat(card.url), colors: card.colors, onClick: card.onClick, images: [
                    {
                        id: card.id,
                        src: "https:".concat((_e = (_d = (_c = (_b = (_a = card.variants[0]) === null || _a === void 0 ? void 0 : _a.images) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.image) === null || _d === void 0 ? void 0 : _d.file) === null || _e === void 0 ? void 0 : _e.url),
                        alt: card.title
                    }
                ], title: card.title, price: (_f = card.variants.find(function (v) { return v.price; })) === null || _f === void 0 ? void 0 : _f.price, withColorSummary: true, forceSmallBreakpoint: true, key: card.id, smallScreenAcross: smallScreenAcross, mediumScreenAcross: mediumScreenAcross, largeScreenAcross: largeScreenAcross, gutter: gutter, totalGridColumns: isTenColumnGrid ? 10 : 12, packDiscountCopy: (_g = siteConfig === null || siteConfig === void 0 ? void 0 : siteConfig.discount_copy) === null || _g === void 0 ? void 0 : _g.value, shopifyId: (_h = card === null || card === void 0 ? void 0 : card.externalSystems) === null || _h === void 0 ? void 0 : _h.shopify, "$styleQueryMode": styleQueryMode, callouts: 'callouts' in card && Array.isArray(card.callouts)
                    ? card.callouts
                    : undefined })) : (React.createElement(ProductCardWithQuickAdd, __assign({ dataLabel: productCardDataLabel, key: card.id, shopifyId: (_j = card === null || card === void 0 ? void 0 : card.externalSystems) === null || _j === void 0 ? void 0 : _j.shopify }, card)));
        }
        /**
         * Handle non-product cards (ContentSystemCard, VideoPlayer, etc.)
         * If the card has a transparent background,
         * use the section's background color
         */
        var isTransparent = 'backgroundColor' in card && card.backgroundColor === 'transparent';
        var backgroundCard = 'backgroundColor' in card
            ? card.backgroundColor
            : 'white';
        if (isTransparent) {
            if (isBackgroundImage) {
                backgroundCard = 'transparent';
            }
            else {
                backgroundCard = backgroundColor;
            }
        }
        var customBackgroundCard = 'customBackgroundColor' in card
            ? card.customBackgroundColor
            : undefined;
        if (isTransparent) {
            if (isBackgroundImage) {
                customBackgroundCard = 'transparent';
            }
            else {
                customBackgroundCard = customBackgroundColor;
            }
        }
        if (isVideoPlayer(card)) {
            var aspectRatio = getUseAspectRatio(card.vimeoId || '');
            return (React.createElement(Styled.CardWrapper, { smallScreenAcross: smallScreenAcross, mediumScreenAcross: mediumScreenAcross, largeScreenAcross: largeScreenAcross, totalGridColumns: isTenColumnGrid ? 10 : 12, key: card.id, aspectRatio: aspectRatio, "$styleQueryMode": styleQueryMode },
                React.createElement(VideoPlayer, { vimeoId: card.vimeoId })));
        }
        // This temporary card identifier forces the responsive text sizes on a card because
        // it does not currently support responsive container design.
        if ('__typename' in card && card.__typename === 'TemporaryCard') {
            return (React.createElement(Styled.Card, __assign({}, card, { primaryHeaderPosition: card.primaryHeaderPosition || card.cardTextPosition, key: 'id' in card ? card.id : cardIndex, backgroundColor: backgroundCard, customBackgroundColor: customBackgroundCard, smallScreenAcross: smallScreenAcross, mediumScreenAcross: smallScreenAcross, largeScreenAcross: smallScreenAcross, totalGridColumns: isTenColumnGrid ? 10 : 12, responsiveTextSizes: [
                    { screenSize: 'SMALL', textSize: 'MINI' },
                    { screenSize: 'MEDIUM', textSize: 'MINI' },
                    { screenSize: 'LARGE', textSize: 'MINI' }
                ], isMaxHeight: isCarousel || (!isCarousel && !isCentered), isCarousel: isCarousel, columnSpan: 'columnSpan' in card ? card.columnSpan : undefined, rowSpan: 'rowSpan' in card ? card.rowSpan : undefined, hoverEffect: 'hoverEffect' in card ? card.hoverEffect : undefined, "$styleQueryMode": styleQueryMode })));
        }
        return (React.createElement(Styled.Card, __assign({}, card, { primaryHeaderPosition: card.primaryHeaderPosition || card.cardTextPosition, dataLabel: "".concat(sectionDataLabel, "_contentCard").concat(cardIndex), backgroundColor: backgroundCard, customBackgroundColor: customBackgroundCard, imageResolutions: isGalleryLayout
                ? getGalleryImageResolutions()
                : contentCardimageResolutionsByLayout.get(currentLayout), key: 'id' in card ? card.id : cardIndex, isTransparent: isTransparent, smallScreenAcross: smallScreenAcross, mediumScreenAcross: mediumScreenAcross, largeScreenAcross: largeScreenAcross, totalGridColumns: isTenColumnGrid ? 10 : 12, responsiveTextSizes: responsiveTextSizes, isMaxHeight: isCarousel || (!isCarousel && !isCentered), isCarousel: isCarousel, columnSpan: 'columnSpan' in card ? card.columnSpan : undefined, rowSpan: 'rowSpan' in card ? card.rowSpan : undefined, hoverEffect: 'hoverEffect' in card ? card.hoverEffect : undefined, "$styleQueryMode": styleQueryMode })));
    });
    var carouselRef = useRef(null);
    var handleClickSlide = function (direction) {
        if (!carouselRef.current)
            return;
        if (direction === 'next') {
            carouselRef.current.slideNext();
        }
        else {
            carouselRef.current.slidePrev();
        }
    };
    var CAROUSEL = useMemo(function () { return (React.createElement(Styled.Carousel, __assign({ ref: carouselRef, controls: carouselArrowMode, onPositionChange: setCurrentSlide, isCentered: isCentered, gutter: gutter, "$styleQueryMode": styleQueryMode }, carouselProps), RENDERED_CARDS)); }, [
        medium,
        large,
        enhancedCardsToDisplay.length,
        isGalleryLayout,
        visibleSlidesCount,
        getUseAspectRatio
    ]);
    var autoColor = textColor === 'AUTO'
        ? Shared.getCompliantColorTheme({
            textColor: textColor,
            backgroundColor: customBackgroundColor || convertColorToThemeHexCode(backgroundColor)
        })
        : null;
    var buttonsExist = ctas === null || ctas === void 0 ? void 0 : ctas.some(function (cta) { return (cta === null || cta === void 0 ? void 0 : cta.ctaStyle) === 'BUTTON'; });
    var ButtonVariant = 'default';
    var ButtonColorMode = textColor === 'LIGHT' ? 'day' : 'night';
    var lightHexCode = autoColor === 'LIGHT' || textColor === 'LIGHT'
        ? convertColorToThemeHexCode('white')
        : undefined;
    var filteredCtas = ctas === null || ctas === void 0 ? void 0 : ctas.filter(function (el) { return el.url && el.text; });
    var hasContent = !!(content || eyebrow);
    var hasNoEnhancedCards = enhancedCardsToDisplay.length === 0;
    return (React.createElement(ThemeProvider, { config: settings, sectionType: "CARD_SECTION" },
        React.createElement(QuickAddModalProvider, null,
            React.createElement(QuickAddModal, null),
            React.createElement(Styled.CardSection, { backgroundColor: backgroundColor, customBackgroundColor: customBackgroundColor, "data-label": sectionDataLabel, hasBackgroundImage: isBackgroundImage, className: className },
                !!largeScreenImage && (React.createElement(Styled.LargeBackgroundImage, { src: "https:".concat(largeScreenImage.file.url), alt: largeScreenImage.description, resolution: {
                        large: CARD_SECTION_IMAGE_RESOLUTIONS.large,
                        xlarge: CARD_SECTION_IMAGE_RESOLUTIONS.xlarge,
                        largest: CARD_SECTION_IMAGE_RESOLUTIONS.xlargest
                    }, crop: {
                        height: largeScreenImage.file.details.image.height,
                        width: largeScreenImage.file.details.image.width
                    }, height: largeScreenImage.file.details.image.height, width: largeScreenImage.file.details.image.width, withProgressiveLoading: false, isLazy: false, maxWidth: largeScreenImage.file.details.image.width, shouldNotCropContent: shouldNotCropContent, "$styleQueryMode": styleQueryMode })),
                !!mediumScreenImage && (React.createElement(Styled.MediumBackgroundImage, { src: "https:".concat(mediumScreenImage.file.url), alt: mediumScreenImage.description, resolution: {
                        medium: CARD_SECTION_IMAGE_RESOLUTIONS.medium
                    }, crop: {
                        height: mediumScreenImage.file.details.image.height,
                        width: mediumScreenImage.file.details.image.width
                    }, height: mediumScreenImage.file.details.image.height, width: mediumScreenImage.file.details.image.width, withProgressiveLoading: false, isLazy: false, maxWidth: mediumScreenImage.file.details.image.width, shouldNotCropContent: shouldNotCropContent, "$styleQueryMode": styleQueryMode })),
                !!smallScreenImage && (React.createElement(Styled.SmallBackgroundImage, { src: "https:".concat(smallScreenImage.file.url), alt: smallScreenImage.description, resolution: {
                        small: CARD_SECTION_IMAGE_RESOLUTIONS.small
                    }, crop: {
                        height: smallScreenImage.file.details.image.height,
                        width: smallScreenImage.file.details.image.width
                    }, height: smallScreenImage.file.details.image.height, width: smallScreenImage.file.details.image.width, withProgressiveLoading: false, isLazy: false, maxWidth: smallScreenImage.file.details.image.width, shouldNotCropContent: shouldNotCropContent, "$styleQueryMode": styleQueryMode })),
                React.createElement(Styled.ContentContainer, { verticalSpacing: verticalSpace, removeVerticalPadding: removeSpace, shouldNotCropContent: shouldNotCropContent, removeHorizontalPadding: isHorizontalSpaceNone, isCarousel: isCarousel, "$withSidePadding": withSidePadding, "$styleQueryMode": styleQueryMode },
                    hasContent && (React.createElement(Styled.ContentHorizontalSpacer, { isHorizontalComfortableSize: isHorizontalComfortableSize, "$styleQueryMode": styleQueryMode, isLargeScreenWithSmallContainerQuery: isLargeScreenWithSmallContainerQuery },
                        React.createElement(Styled.ContentInner, { hasNoEnhancedCards: hasNoEnhancedCards },
                            React.createElement(Styled.TextContainer, { className: "header-above-grid", textAlignment: cardheaderAlignment || 'LEFT', horizontalAlignment: horizontalAlignment, textColor: textColor, backgroundColor: backgroundColor, customBackgroundColor: customBackgroundColor, smallScreenAcross: smallScreenAcross, isGalleryLayout: isGalleryLayout, isCarousel: isCarousel, headerMaxLineLength: headerMaxLineLength, isCentered: isCentered, "$styleQueryMode": styleQueryMode },
                                Boolean(breadcrumb === null || breadcrumb === void 0 ? void 0 : breadcrumb.length) && (React.createElement(Styled.Breadcrumbs, { dataLabel: breadcrumbDataLabel, textColor: lightHexCode, crumbs: breadcrumb })),
                                eyebrow && (React.createElement(SecondaryHeader, { lightHexCode: lightHexCode }, eyebrow)),
                                content && (React.createElement(TextToJSX, { text: content || '', lightHexCode: lightHexCode, headerMaxLineLength: headerMaxLineLength }))),
                            carouselArrowMode === 'external' && (React.createElement(Styled.SelectCarousel, { withNumbers: false, currentSlide: currentSlide, totalSlides: effectiveTotalSlides, onLeftArrowClick: function () {
                                    handleClickSlide('previous');
                                }, onRightArrowClick: function () {
                                    handleClickSlide('next');
                                }, size: isLarge ? 'large' : 'small' }))))),
                    !isCarousel && (React.createElement(Styled.Cards, { isTenColumnGrid: isTenColumnGrid, isHorizontalComfortableSize: isHorizontalComfortableSize, isHorizontalSpaceNone: isHorizontalSpaceNone, hasIndentGrid: indentGrid, styleQueryMode: styleQueryMode, className: isHorizontalComfortableSize ? 'comfortable' : '', horizontalAlignment: horizontalAlignment, isGalleryLayout: isGalleryLayout, verticalAlignment: verticalAlignment, hasIndentGallery: indentGallery, gutter: gutter, smallScreenAcross: smallScreenAcross, mediumScreenAcross: mediumScreenAcross, largeScreenAcross: largeScreenAcross }, RENDERED_CARDS)),
                    isCarousel && (React.createElement(Styled.CarouselWrapper, { isHorizontalComfortableSize: isHorizontalComfortableSize, hasIndentGrid: indentGrid, gutter: gutter, "$styleQueryMode": styleQueryMode }, CAROUSEL)),
                    Boolean(filteredCtas.length) && (React.createElement(Styled.SectionCTASpacer, { isHorizontalComfortableSize: isHorizontalComfortableSize, styleQueryMode: styleQueryMode },
                        React.createElement(Styled.SectionCTAContainer, null,
                            React.createElement(Styled.SectionCTAWrapper, null,
                                React.createElement(Shared.CTAs, null, filteredCtas === null || filteredCtas === void 0 ? void 0 : filteredCtas.map(function (_a, i) {
                                    var url = _a.url, text = _a.text, _b = _a.ctaStyle, ctaStyle = _b === void 0 ? 'LINK' : _b, displayLinked = _a.displayLinked;
                                    return (React.createElement(NextLink, { href: url, key: "".concat(url).concat(text).concat(i), target: displayLinked === 'NEW_TAB' ? '_blank' : '_self', rel: displayLinked ? 'noopener noreferrer' : undefined },
                                        React.createElement(Shared.CTA, { "data-label": "".concat(sectionDataLabel, "_CTA").concat(i + 1), lightHexCode: lightHexCode, ctaStyle: buttonsExist ? 'BUTTON' : ctaStyle, size: "medium", variant: ButtonVariant, colorMode: ButtonColorMode }, text)));
                                })))))))))));
};
CardSection.fragment = CardSectionFragment;
export default CardSection;
